import { gzipSync, gunzipSync, unzipSync, strToU8, strFromU8 } from 'fflate';
import { KINDS } from '../sync/schema.js';
import { getDeviceId } from '../db/meta.js';

export const FORMAT = 'recall-backup';
export const VERSION = 1;

export class BackupFormatError extends Error {}

function pickSynced(kind, row) {
  const out = { id: row.id, _hlc: { ...(row._hlc ?? {}) } };
  for (const key of KINDS[kind].keys) if (key in row) out[key] = row[key];
  return out;
}

/** Full copy of all replicated data (including tombstones and HLC metadata). */
export async function buildSnapshot(db, { reason = 'manual' } = {}) {
  const deviceId = await getDeviceId(db);
  const tables = {};
  await db.transaction('r', Object.values(KINDS).map((d) => db[d.table]), async () => {
    for (const [kind, def] of Object.entries(KINDS)) {
      tables[kind] = (await db[def.table].toArray()).map((r) => pickSynced(kind, r));
    }
  });
  const snap = { format: FORMAT, version: VERSION, createdAt: Date.now(), deviceId, reason, tables };
  snap.counts = summarize(snap);
  return snap;
}

/** Human-meaningful counts (live items only). */
export function summarize(snap) {
  const t = snap.tables ?? {};
  const live = (rows) => (rows ?? []).filter((r) => !r.deletedAt).length;
  return {
    decks: live(t.deck),
    notes: live(t.note),
    cards: live(t.card),
    reviews: live(t.reviewLog),
  };
}

export function isEmptySnapshot(snap) {
  const c = summarize(snap);
  return !c.decks && !c.notes && !c.cards && !c.reviews;
}

export function validateSnapshot(snap) {
  if (!snap || typeof snap !== 'object') throw new BackupFormatError('Not a backup file.');
  if (snap.format !== FORMAT) throw new BackupFormatError('This file is not a Recall backup.');
  if (typeof snap.version !== 'number' || snap.version > VERSION) {
    throw new BackupFormatError('This backup was made by a newer version of Recall. Update the app first.');
  }
  if (!snap.tables || typeof snap.tables !== 'object') throw new BackupFormatError('Backup is missing data.');
  for (const kind of Object.keys(KINDS)) {
    const rows = snap.tables[kind] ?? [];
    if (!Array.isArray(rows)) throw new BackupFormatError(`Backup data for ${kind} is invalid.`);
    for (const r of rows) {
      if (typeof r?.id !== 'string' || typeof r._hlc !== 'object') {
        throw new BackupFormatError(`Backup contains an invalid ${kind} entry.`);
      }
    }
  }
  return snap;
}

export function encodeSnapshot(snap) {
  return gzipSync(strToU8(JSON.stringify(snap)), { level: 6 });
}

/** Accepts gzip (.json.gz), zip (.flashcards.zip) or plain JSON bytes. */
export function decodeSnapshot(bytes) {
  let text;
  try {
    if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
      text = strFromU8(gunzipSync(bytes));
    } else if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
      const files = unzipSync(bytes);
      if (!files['data.json']) throw new BackupFormatError('Zip file has no data.json.');
      text = strFromU8(files['data.json']);
    } else {
      text = strFromU8(bytes);
    }
    return validateSnapshot(JSON.parse(text));
  } catch (e) {
    if (e instanceof BackupFormatError) throw e;
    throw new BackupFormatError(`Could not read backup: ${e.message}`);
  }
}
